import { InitEnvironmentVariable, SchoolReportService, ClassMonthlyReportService, ClassMonthlyReportBySchoolIdAndMonth } from '@PikaDoApp/npm-pikado-logic';
import { getEnvironment } from '../../../lib/getEnvironment';

/**
 * Monthly average performance record
 */
export interface PastMonthlyAverage {
  average: number;
  month: string;
}

/**
 * Comprehensive school statistics including lifetime performance metrics
 */
export interface SchoolStatistic {
  _id: string;
  createdAt: string;
  schoolId: string;
  schoolLifeTimeAverage: number;
  totalStudentsInSchool: number;
  updatedAt: string;
  pastMonthlyAverageList: PastMonthlyAverage[];
}

/**
 * Daily engagement record
 */
export interface SchoolDailyRecord {
  date: string;
  exercisesAnsweredCount: number;
  studentsEngagedCount: number;
  studentIds: string[];
}

/**
 * Monthly school report with daily records and engagement metrics
 */
export interface SchoolReportsByMonth {
  id: string;
  month: string;
  schoolId: string;
  schoolMonthlyAverage: number;
  totalExercisesThisMonth: number;
  totalStudentsEngagedThisMonth: number;
  totalStudentsInSchoolThisMonth: number;
  schoolDailyRecord: SchoolDailyRecord[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Re-export ClassMonthlyReportBySchoolIdAndMonth from npm-pikado-logic for convenience
 */
export type ClassMonthlyReport = ClassMonthlyReportBySchoolIdAndMonth;

/**
 * Service wrapper for school statistics from npm-pikado-logic
 * This service fetches school-level statistics including lifetime averages and monthly performance
 */
export class SchoolStatisticServiceWrapper {
  private schoolReportService: SchoolReportService;
  private classMonthlyReportService: ClassMonthlyReportService;

  constructor() {
    InitEnvironmentVariable(getEnvironment());
    this.schoolReportService = new SchoolReportService();
    this.classMonthlyReportService = new ClassMonthlyReportService();
  }

  /**
   * Get school statistics including lifetime average and monthly performance data
   * @param schoolId - School ID
   * @returns SchoolStatistic or null
   */
  async getSchoolStatistic(schoolId: string): Promise<SchoolStatistic | null> {
    if (!schoolId) {
      console.warn('No schoolId provided for school statistic');
      return null;
    }

    try {
      const statistic = await this.schoolReportService.getSchoolStatistic(schoolId);
      return statistic;
    } catch (error) {
      console.error('Error in getSchoolStatistic:', error);
      throw error;
    }
  }

  /**
   * Get school reports by month with daily records and engagement metrics
   * @param month - Month in YYYY-MM format (e.g., "2024-10")
   * @param schoolId - School ID
   * @returns SchoolReportsByMonth or null
   */
  async getSchoolReportsByMonth(
    month: string,
    schoolId: string
  ): Promise<SchoolReportsByMonth | null> {
    if (!schoolId || !month) {
      console.warn('No schoolId or month provided for monthly report');
      return null;
    }

    try {
      const report = await this.schoolReportService.getSchoolReportsByMonth(month, schoolId);
      return report;
    } catch (error) {
      console.error('Error in getSchoolReportsByMonth:', error);
      throw error;
    }
  }

  /**
   * Get all class monthly reports for a school by month (batch API)
   * Uses ClassMonthlyReportService from npm-pikado-logic
   * @param schoolId - School ID
   * @param month - Month in YYYY-MM format
   * @returns Array of ClassMonthlyReport or empty array
   */
  async getClassesMonthlyReportsBySchoolIdAndMonth(
    schoolId: string,
    month: string
  ): Promise<ClassMonthlyReport[]> {
    if (!schoolId || !month) {
      console.warn('No schoolId or month provided for classes monthly reports');
      return [];
    }

    try {
      const reports = await this.classMonthlyReportService.getClassesMonthlyReportsBySchoolIdAndMonth(
        month,
        schoolId
      );

      return reports || [];
    } catch (error) {
      console.error('Error in getClassesMonthlyReportsBySchoolIdAndMonth:', error);
      return [];
    }
  }
}
